import React, { ComponentProps, useState } from 'react';
import Control from './Control';
import FaqFooter from '../FaqFooter';
import ProcessInformationScreen from '../ProcessInformationScreen';
import { Consumer } from '../../util/enums';
import { SnapStepper } from '../SnapStepper';

export interface StepperPage {
  component: React.JSX.Element;
  nextText?: string;
  isValid: boolean;
  navigation: {
    showBack: boolean;
    showForward: boolean;
    showSteps: boolean;
    onSkip?: () => void;
    onFinish?: () => void;
    finishIsValid?: boolean;
  };
  beforeNext?: () => Promise<void>;
  onCancel?: () => void;
  shouldRender: boolean;
  srName: string;
}

export default function Stepper({
  pages,
  showSubmitForReviewButton,
  embedded,
  consumer = Consumer.RAISE,
  shadow = true,
  showFaq = true,
}: {
  pages: StepperPage[];
  showSubmitForReviewButton?: boolean;
  embedded?: boolean;
  consumer?: Consumer;
  shadow?: boolean;
  showFaq?: boolean;
}): React.JSX.Element {
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  let divWrapperClass = embedded ? '' : 'bg-white rounded';
  if (shadow) {
    divWrapperClass += 'shadow-lg';
  }

  const steps: ComponentProps<typeof SnapStepper>['steps'] = pages.map(
    (page, index) => ({
      name: page.srName,
      isCurrent: index === currentStepIndex,
      isComplete: index < currentStepIndex,
    }),
  );

  return (
    <div className='min-h-screen flex justify-center'>
      <div className='w-full'>
        <div className={divWrapperClass}>
          <div className='grid text-sm grid-cols-1 lg:grid-cols-3'>
            <div className='lg:col-span-3'>
              {!isLoading &&
                steps.length > 1 &&
                pages[currentStepIndex].navigation.showSteps && (
                  <SnapStepper steps={steps} />
                )}
              <div>
                {pages.map((_, index) => (
                  <div
                    key={`step_${index}`}
                    style={{
                      display:
                        currentStepIndex === index &&
                        !isLoading &&
                        pages[currentStepIndex].shouldRender
                          ? 'initial'
                          : 'none',
                    }}
                  >
                    {pages[index].component}
                  </div>
                ))}
                {(isLoading || !pages[currentStepIndex].shouldRender) && (
                  <ProcessInformationScreen />
                )}
                {!isLoading && (
                  <Control
                    currentStepIndex={currentStepIndex}
                    pages={pages}
                    onStepIndexChange={setCurrentStepIndex}
                    onLoadingChange={setIsLoading}
                    showSubmitForReviewButton={showSubmitForReviewButton}
                  />
                )}
              </div>
            </div>
          </div>
          {consumer === Consumer.RAISE && showFaq && !isLoading && (
            <FaqFooter
              showText={
                (isLoading && currentStepIndex > 1) ||
                currentStepIndex < pages.length - 1
              }
              showLinks={currentStepIndex === 0}
            />
          )}
        </div>
      </div>
    </div>
  );
}
